
import { useState } from "react";
import { Navigation } from "@/components/Navigation";
import { CreateResourceDialog } from "@/components/CreateResourceDialog";
import { ResourcesLoading } from "@/components/resource/ResourcesLoading";
import { ResourcesError } from "@/components/resource/ResourcesError";
import { useAuth, useResources } from "@/hooks/useResourcesData";
import { useProfile } from "@/hooks/useProfile";
import { useIsMobile } from "@/hooks/use-mobile";
import { PasswordPromptHandler } from "@/components/auth/PasswordPromptHandler";
import { useResourceProgressInitializer } from "@/hooks/useResourceProgressInitializer";
import { useSessionLogger } from "@/hooks/useSessionLogger";
import { TraineeResourcesView } from "@/components/resource/TraineeResourcesView";
import { AdminResourcesView } from "@/components/resource/AdminResourcesView";

const MyTrainingResources = () => {
  const [isRetrying, setIsRetrying] = useState(false);
  const { data: session, isLoading: isAuthLoading, isNewUser } = useAuth();
  const { data: profile } = useProfile(session?.user?.id);
  const { 
    data: resources, 
    isLoading: isResourcesLoading,
    error: resourcesError,
    refetch: refetchResources
  } = useResources(session?.user?.id);
  const isMobile = useIsMobile();

  // Use our new custom hooks
  useSessionLogger(session, isNewUser);
  
  const isTrainee = profile?.role === 'trainee';
  
  // Initialize resource progress for trainees
  useResourceProgressInitializer(session?.user?.id, resources, isTrainee);

  // Check if the error is likely a Firebase/connection issue
  const isConnectionError = resourcesError && (
    String(resourcesError).includes('firebase') || 
    String(resourcesError).includes('firestore') || 
    String(resourcesError).includes('Failed to fetch') ||
    String(resourcesError).includes('NetworkError')
  );

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await refetchResources();
    } catch (err) {
      console.error("Error refetching resources:", err);
    } finally {
      setIsRetrying(false);
    }
  };

  if (isAuthLoading || isResourcesLoading) {
    return <ResourcesLoading />;
  }

  if (resourcesError) {
    return (
      <ResourcesError 
        message={isConnectionError 
          ? "Connection issue while loading resources" 
          : "Error loading training resources"}
        onRetry={handleRetry}
        showNotification={true}
        autoRetry={isConnectionError}
        isFirebaseError={isConnectionError}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 pt-24">
        <PasswordPromptHandler session={session} isNewUser={isNewUser}>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold tracking-tight`}>
              {isTrainee ? 'My Training Resources' : 'Training Resources'}
            </h1>
            <div className="flex items-center gap-2">
              {!isTrainee && <CreateResourceDialog />}
            </div>
          </div>
          
          {isTrainee ? (
            <TraineeResourcesView resources={resources} />
          ) : (
            <AdminResourcesView resources={resources} />
          )}
        </PasswordPromptHandler>
      </div>
    </div>
  );
};

export default MyTrainingResources;
